import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { firebaseApp } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useVerificationStatus } from "../hooks/useVerificationStatus";
import { Briefcase, CheckCircle, Clock, ExternalLink, Link2, PauseCircle, ShieldCheck, Sparkles, UserCheck, XCircle } from "lucide-react";
import toast from "react-hot-toast";
import { savePendingTest } from "../utils/pendingTestStorage";

interface SharedTestDoc {
  id: string;
  jobTitle?: string;
  hrId?: string;
  hrEmail?: string;
  createdAt?: any;
  isEnabled?: boolean;
  config?: {
    duration?: number;
    questionCount?: number;
    difficulty?: string;
  };
  testData?: {
    title?: string;
    description?: string;
  };
}

const SharedTest: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isVerified, loading: verificationLoading } = useVerificationStatus();
  const [test, setTest] = useState<SharedTestDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasTaken, setHasTaken] = useState<boolean>(false);
  const [checkingAttempt, setCheckingAttempt] = useState(true);

  const db = getFirestore(firebaseApp);

  const isJobSeeker = user?.userType === "jobseeker";
  const isHROrAdmin = user && user.userType !== "jobseeker";

  useEffect(() => {
    const fetchTest = async () => {
      if (!testId) return;
      try {
        const snap = await getDoc(doc(db, "hrTests", testId));
        if (snap.exists()) {
          setTest({ id: snap.id, ...(snap.data() as any) });
        } else {
          setTest(null);
        }
      } catch (error) {
        toast.error("Could not load the test link.");
      } finally {
        setLoading(false);
      }
    };

    fetchTest();
  }, [testId, db]);

  useEffect(() => {
    const checkAttempt = async () => {
      if (!user || !isJobSeeker || !testId) {
        setHasTaken(false);
        setCheckingAttempt(false);
        return;
      }

      try {
        const q = query(
          collection(db, "jobseekerResults"),
          where("jobSeekerId", "==", user.id),
          where("testId", "==", testId)
        );
        const snap = await getDocs(q);
        setHasTaken(!snap.empty);
      } catch (error) {
      } finally {
        setCheckingAttempt(false);
      }
    };

    checkAttempt();
  }, [user, isJobSeeker, testId, db]);

  const shareUrl = useMemo(() => `${window.location.origin}/test/share/${testId}`, [testId]);

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch (error) {
      toast.error("Couldn't copy. Please copy manually.");
    }
  };

  // Helper to save pending test before navigation
  const saveTestAndNavigate = (path: string) => {
    if (testId) {
      // Always save the pending test with testId - even if test data hasn't fully loaded
      savePendingTest(
        testId,
        test?.testData?.title || test?.jobTitle,
        test?.hrEmail
      );
    } else {
    }
    navigate(path);
  };

  const handleStart = () => {
    if (!testId) return;
    if (!user) {
      saveTestAndNavigate("/signup/jobseeker");
      return;
    }
    if (!isJobSeeker) {
      toast.error("Only jobseekers can take this test.");
      return;
    }
    // HR-shared tests bypass verification requirement
    // Add source=hr-shared to indicate this is from a shared link
    navigate(`/job-seeker/take-test/${testId}?source=hr-shared`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading test...</p>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center border border-gray-100">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Oops! Test Not Found</h1>
          <p className="text-gray-600 mb-6">
            This test link may be invalid or has expired. Please contact the HR who shared it with you for a new link.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={() => navigate("/login")}
              className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors font-medium"
            >
              Go to Login
            </button>
            <button
              onClick={() => navigate("/signup/jobseeker")}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Sign up
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if the test is disabled (hiring paused)
  if (test.isEnabled === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center border border-gray-100">
          <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <PauseCircle className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Hiring Paused</h1>
          <p className="text-gray-600 mb-2">
            The hiring process for <span className="font-semibold text-gray-800">{test.testData?.title || test.jobTitle || "this position"}</span> has been temporarily paused.
          </p>
          <p className="text-gray-500 text-sm mb-6">
            Don't worry! There are plenty of other exciting opportunities waiting for you on our platform.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            {!user ? (
              <>
                <button
                  onClick={() => navigate("/signup/jobseeker")}
                  className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors font-medium inline-flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Join & Explore Jobs
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Already a member? Login
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate(isJobSeeker ? "/job-seeker/available-tests" : "/hr/dashboard")}
                className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors font-medium inline-flex items-center justify-center gap-2"
              >
                <Briefcase className="w-4 h-4" />
                {isJobSeeker ? "Browse Other Opportunities" : "Go to Dashboard"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show clean login/signup screen for unauthenticated users
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-gray-100">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Smarter hiring with VetriaAI
          </h1>
          <p className="text-gray-600 mb-6">
            Please sign up to access this test.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={() => saveTestAndNavigate("/login")}
              className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors font-medium"
            >
              Go to Login
            </button>
            <button
              onClick={() => saveTestAndNavigate("/signup/jobseeker")}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Sign up
            </button>
          </div>
        </div>
      </div>
    );
  }

  // HR-shared tests bypass verification - user can take if they're a jobseeker and haven't taken it
  const canTake =
    isJobSeeker && !checkingAttempt && !hasTaken;

  const statusBadge = () => {
    if (hasTaken) {
      return (
        <div className="flex items-center gap-2 text-yellow-800 bg-yellow-50 border border-yellow-200 px-3 py-2 rounded-lg text-sm">
          <CheckCircle className="w-4 h-4" />
          You have already completed this test.
        </div>
      );
    }
    if (!isJobSeeker && user) {
      return (
        <div className="flex items-center gap-2 text-blue-800 bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg text-sm">
          <ShieldCheck className="w-4 h-4" />
          Logged in as {user.userType}. You can preview but cannot take this test.
        </div>
      );
    }
    // No verification warning for HR-shared tests - they bypass verification
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto px-4 py-12 space-y-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">Shared Test Link</p>
            <h1 className="text-3xl font-bold text-gray-900">
              {test.testData?.title || test.jobTitle || "Shared Test"}
            </h1>
            <p className="text-gray-600 mt-2">
              Invited by {test.hrEmail || "an HR manager"}. You can take this assessment directly.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={copyShareLink}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Link2 className="w-4 h-4" />
              Copy Link
            </button>
            <button
              onClick={() => window.open(shareUrl, "_blank")}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Open Link
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <UserCheck className="w-5 h-5 text-red-800" />
                <span className="text-sm text-gray-600">
                  {test.hrEmail ? `Created by ${test.hrEmail}` : "Shared by an HR manager"}
                </span>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {test.jobTitle || "Role Assessment"}
              </h2>
              <p className="text-gray-600">
                {test.testData?.description ||
                  "Complete this assessment to showcase your skills for this opportunity."}
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-xs text-gray-500">Duration</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {test.config?.duration ? `${test.config.duration} mins` : "Not specified"}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-xs text-gray-500">Questions</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {test.config?.questionCount ?? "Not specified"}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-xs text-gray-500">Difficulty</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize">
                    {test.config?.difficulty || "Not set"}
                  </p>
                </div>
              </div>

              {statusBadge()}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                <Clock className="w-4 h-4" />
                One attempt per candidate
              </div>
              {isJobSeeker && (
                <>
                  <p className="text-gray-700 mb-4">
                    {hasTaken
                      ? "You have already completed this test. You can review your results."
                      : "You're invited to take this assessment. Good luck!"}
                  </p>
                  <button
                    onClick={hasTaken ? () => navigate("/job-seeker/results") : handleStart}
                    disabled={checkingAttempt || hasTaken}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      canTake
                        ? "bg-red-800 text-white hover:bg-red-900"
                        : "bg-gray-200 text-gray-700 cursor-not-allowed"
                    }`}
                  >
                    {hasTaken ? "View Results" : canTake ? "Start Test" : "Loading..."}
                  </button>
                  {false && !hasTaken && (
                    <button
                      onClick={() => saveTestAndNavigate("/job-seeker/skills-validation")}
                      className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <ShieldCheck className="w-4 h-4" />
                      Complete Skills Verification
                    </button>
                  )}
                </>
              )}

              {user && isHROrAdmin && (
                <>
                  <p className="text-gray-700 mb-4">
                    You are logged in as {user.userType}. You can preview the test but cannot attempt it.
                  </p>
                  <button
                    onClick={() => navigate("/hr/my-tests")}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <EyeIcon />
                    View in My Tests
                  </button>
                </>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">How this works</h3>
              <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>You've been invited to take this assessment directly.</li>
                <li>Each test can be taken once per candidate.</li>
                <li>Your progress may be monitored for integrity.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EyeIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  <circle cx="12" cy="12" r="3" />
</svg>;

export default SharedTest;
